import { BatchDomain, DocumentClassKey } from '@ps-gui/claims-terminal-shared';
import { FieldGroupModel, TableModel } from './extraction-models/extraction-models';
import { Type } from '@angular/core';
import { FormArray, FormControl, ValidatorFn } from '@angular/forms';
import { ExtractedRowForm } from './extraction-form-models';
import { BaseWorkflowHookService } from '../service/workflow-hooks/base-workflow-hook.service';

export type DataAwareValidator = (
  control: FormControl,
  extractedRowFormProvider: () => FormArray<ExtractedRowForm> | undefined,
) => ValidatorFn;

export type ExtractionSection = FieldGroupModel | TableModel;

export const isFieldGroupModel = (section: ExtractionSection): section is FieldGroupModel =>
  section.type === 'fieldGroupModel';

export const isTableModel = (section: ExtractionSection): section is TableModel =>
  section.type === 'tableModel';

export enum ExtractionSectionViewType {
  SCROLLABLE,
}

export interface ExtractionPage {
  sections: ExtractionSection[];
  viewType: ExtractionSectionViewType;
}

export interface ExtractionConfiguration {
  extractionPages: ExtractionPage[];
}

/**
 * If a split configuration is set for a domain and the resulting
 * split entity ids are saved in local storage (say in a split hook),
 * multiple consolidation views are created - one for each split copy.
 */
interface BatchSplitConfiguration {
  /**
   * The document class which has to occur multiple times for a batch to be split.
   * Each split is associated with all documents of other classes and exactly one
   * document of this class.
   */
  splitInducingDocumentClass: DocumentClassKey;

  /**
   * Normally, in the consolidation view, a field is marked as "unconsolidated"
   * (for now this means "empty") when multiple values (from different documents)
   * where extracted for it.
   *
   * For some fields (defined by this property) this does not make sense, this is
   * the case for fields which are unique for each split-inducing document, they
   * are "split scoped" (e.g., a total sum for an invoice document).
   */
  splitScopedFieldKeys: string[];

  splitScopedTableKeys: string[];
}

export interface DomainConfiguration {
  extractionConfigurationByClass: Partial<Record<DocumentClassKey, ExtractionConfiguration>>;
  consolidation?: {
    consolidatedDocumentClasses: DocumentClassKey[];
    batchClassification?: { batchTypes: { label: string; value: string }[]; batchTypeKey: string };
    sections: FieldGroupModel[];
  };
  batchSplitConfiguration?: BatchSplitConfiguration;
  workflowHookService: Type<BaseWorkflowHookService>;
  extractionDocumentClassesReceivedAfterSubmission: DocumentClassKey[];
}

export interface ExtractionConfigurationByDomain {
  [BatchDomain.PROOF]: DomainConfiguration;
}
